import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scrollTo";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const BARS = [0.5, 0.9, 0.65, 1, 0.4, 0.8, 0.55, 0.95, 0.7, 0.45, 0.85, 0.6, 1, 0.5, 0.75, 0.9, 0.4, 0.7, 0.55, 0.95, 0.6, 0.8, 0.45, 0.7];

const TimelineCard = ({ rotateX, rotateY }) => (
  <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative [perspective:1200px]">
    <div className="rounded-2xl overflow-hidden border border-gray-200 bg-[#0E0E12] shadow-[0_40px_100px_rgba(10,10,12,0.25)]">
      <div className="relative">
        <img
          src="https://images.unsplash.com/photo-1614963326505-843868e1d83a?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Editing timeline on studio monitor"
          className="w-full h-44 md:h-52 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E12] via-transparent to-black/40" />
        <div className="absolute top-3 left-3 flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/80">
          <span className="w-2 h-2 rounded-full bg-crimson animate-pulse" />
          REC · 00:04:17:08
        </div>
        <div className="absolute top-3 right-3 font-mono text-[10px] tracking-widest text-white/60">4K · 24FPS</div>
      </div>
      <div className="relative px-5 pb-6 pt-4">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10px] tracking-[0.2em] text-white/40">TIMELINE — V1 A1</span>
          <span className="font-mono text-[10px] tracking-widest text-crimson">MASTERING</span>
        </div>
        <div className="relative h-16 flex items-end gap-[3px]">
          {BARS.map((h, i) => (
            <motion.span
              key={i}
              className="flex-1 rounded-sm bg-gradient-to-t from-crimson/40 to-crimson origin-bottom"
              style={{ height: `${h * 100}%` }}
              animate={{ scaleY: [1, 0.25, 1] }}
              transition={{ repeat: Infinity, duration: 1.4, delay: i * 0.07, ease: "easeInOut" }}
            />
          ))}
          <motion.span
            className="absolute -top-2 bottom-0 w-px bg-white/90 shadow-[0_0_12px_rgba(255,255,255,0.8)]"
            animate={{ left: ["4%", "96%"] }}
            transition={{ repeat: Infinity, duration: 9, ease: "linear" }}
          />
        </div>
        <div className="mt-4 flex gap-[3px]">
          {["#FF2A3B", "#F59E0B", "#00F0FF", "#26262e", "#26262e", "#FF2A3B", "#26262e", "#F59E0B"].map((c, i) => (
            <span key={i} className="h-1.5 rounded-full flex-1" style={{ background: c }} />
          ))}
        </div>
      </div>
    </div>

    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
      className="absolute left-4 top-10 glass-pill rounded-full px-4 py-2 font-mono text-[11px] text-gray-800 z-10 shadow-soft hidden xl:block"
      style={{ transform: "translateZ(50px)" }}
    >
      1,200+ Cuts Rendered
    </motion.div>
    <motion.div
      animate={{ y: [0, 8, 0] }}
      transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 0.6 }}
      className="absolute right-4 bottom-14 glass-pill rounded-full px-4 py-2 font-mono text-[11px] text-gray-800 z-10 shadow-soft hidden xl:block"
      style={{ transform: "translateZ(60px)" }}
    >
      Any Budget Tier
    </motion.div>
  </motion.div>
);

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-24"
    >
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_75%_15%,rgba(255,42,59,0.07),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(40%_40%_at_10%_85%,rgba(245,158,11,0.06),transparent_60%)]" />
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <h1 className="font-display font-extrabold tracking-tight text-gray-900 leading-[0.95] text-[clamp(2.4rem,5.8vw,4.6rem)] mt-7">
            <Line delay={0.15}>WE EDIT.</Line>
            <Line delay={0.27}>WE ANIMATE.</Line>
            <Line delay={0.39}>WE PRODUCE.</Line>
            <Line delay={0.51}>
              <span className="text-crimson">YOUR VISION.</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-7 text-gray-500 text-base md:text-lg leading-relaxed max-w-xl"
          >
            Full-spectrum video production, high-octane editing and 2D/3D animation for elite creators,
            YouTube networks and high-growth brands.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.88, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              data-testid="hero-cta-button"
              onClick={() => scrollToId("brief")}
              className="btn-glow inline-flex items-center gap-2 px-8 py-4 rounded-full bg-crimson text-white font-bold text-sm tracking-wide"
            >
              Submit Your Brief
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="hero-showreel-button"
              onClick={() => scrollToId("work")}
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full border border-gray-200 bg-white text-gray-900 font-semibold text-sm tracking-wide shadow-soft hover:border-crimson/50 transition-colors duration-300"
            >
              <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-900 group-hover:bg-crimson group-hover:text-white transition-colors duration-300">
                <Play className="w-3.5 h-3.5 fill-current" />
              </span>
              Explore Our Work
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: cardY }} className="lg:col-span-5 hidden sm:block">
          <TimelineCard rotateX={rotateX} rotateY={rotateY} />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.3em] text-gray-400 z-10"
      >
        SCROLL
        <div className="w-px h-8 bg-gradient-to-b from-crimson to-transparent mx-auto mt-3" />
      </motion.div>
    </section>
  );
}
